<template>
  <CCard class="mb-3">
    <CCardHeader>
      <div class="d-flex align-items-center">
        <CIcon name="cipUserPlus" size="sm" class="mr-2"/>
        <h5 class="mb-0">
          <translate translate-context="collaborators.invite">Invite collaborator</translate>
        </h5>
      </div>
    </CCardHeader>
    
    <CCardBody>
      <div class="mb-3">
        <label class="text-muted small">
          <translate translate-context="collaborators.invite">Type multiple emails separated by commas or paste a list of emails. Search for existing collaborators by name or email (min 3 chars). Use ↑/↓ to navigate suggestions, Tab to select first suggestion, Enter to add.</translate>
        </label>
        
        <div class="email-tags-container">
          <div class="email-tags">
            <span v-for="(email, index) in emails" :key="index" class="email-tag">
              {{ email }}
              <button type="button" class="tag-remove" @click="removeEmail(index)">
                <CIcon name="cilX" size="sm"/>
              </button>
            </span>
            <input
              ref="emailInput"
              v-model="currentInput"
              type="text"
              class="tag-input"
              :placeholder="emails.length === 0 ? $gettext('name@example.com') : ''"
              @keydown.enter.prevent="handleAddEmail"
              @keydown.backspace="handleBackspace"
              @keydown="handleSpecialKeys"
              @input="handleInput"
              @paste="handlePaste"
            />
          </div>
          
          <!-- Dropdown for search suggestions -->
          <div v-if="suggestions.length > 0" class="suggestions-dropdown">
            <div
              v-for="(suggestion, index) in suggestions"
              :key="suggestion.id"
              class="suggestion-item"
              :class="{ 'selected': index === selectedSuggestionIndex }"
              @click="selectSuggestion(suggestion)"
              @mouseenter="selectedSuggestionIndex = index"
            >
              <strong>{{ suggestion.first_name }} {{ suggestion.last_name }}</strong>
              <span class="text-muted ml-2">{{ suggestion.email }}</span>
            </div>
          </div>
        </div>
        
        <div v-if="emailError" class="text-danger small mt-1">
          {{ emailError }}
        </div>
      </div>
      
      <CRow>
        <CCol lg="6">
          <h6 class="mb-2">
            <translate translate-context="collaborators.invite">Permissions</translate>
          </h6>
          <div class="permissions-list">
            <div class="d-flex flex-wrap align-items-start">
              <div
                v-for="role in displayedRoles"
                :key="role.id"
                class="d-inline-flex align-items-center mr-2 mb-2"
              >
                <button
                  type="button"
                  class="role-button"
                  :class="{'role-active': selectedRoles[role.id]}"
                  @click="$set(selectedRoles, role.id, !selectedRoles[role.id])"
                >
                  <span class="role-indicator"></span>
                  <CIcon v-if="role.scope" :name="CollaboratorScope.icon(role.scope)" size="sm" class="mr-1" />
                  <span class="role-name">{{ role.name }}</span>
                </button>
                <CButton
                  color="link"
                  size="sm"
                  class="p-1 ml-1"
                  @click="showRolePermissions(role)"
                  v-c-tooltip="$gettext('View permissions')"
                >
                  <CIcon name="cilInfo" size="sm"/>
                </CButton>
              </div>
              
              <!-- Pulsante per mostrare più ruoli -->
              <button
                v-if="hasMoreRoles"
                type="button"
                class="btn btn-sm btn-link p-1 mb-2"
                @click="openRoleSelectionModal"
                v-c-tooltip="{
                  content: `${additionalRolesCount} ${$gettext('more roles available')}`,
                  placement: 'top'
                }"
              >
                <CIcon name="cilOptions" size="lg"/>
              </button>
            </div>
            <div v-if="availableRoles.length === 0" class="text-muted">
              <translate translate-context="collaborators.invite">No roles available</translate>
            </div>
          </div>
        </CCol>
        
        <CCol lg="6">
          <div v-if="canBypassAccept">
            <h6 class="mb-2">
              <translate translate-context="collaborators.invite">Account Creation</translate>
            </h6>
            <div class="alert alert-info" role="alert">
              <small>
                <translate translate-context="collaborators.invite">The account will be created automatically if it doesn't exist.</translate>
                <br>
                <translate translate-context="collaborators.invite">The user will be granted access immediately without email confirmation.</translate>
              </small>
            </div>
            <div class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                id="requireAcceptance"
                v-model="requireAcceptance"
              >
              <label class="form-check-label" for="requireAcceptance">
                <translate translate-context="collaborators.invite">Send invitation email (require acceptance)</translate>
              </label>
            </div>
            <div v-if="requireAcceptance" class="mt-3">
              <label class="text-muted small" v-translate>
                Personal Message (Optional)
              </label>
              <CTextarea
                v-model="personalMessage"
                :placeholder="$gettext('Add a personal note for the invitation email...')"
                :maxlength="1000"
                rows="3"
              />
              <small class="text-muted">
                {{ personalMessage.length }}/1000
              </small>
            </div>
          </div>
          <div v-else>
            <h6 class="mb-2" v-translate>
              Personal Message
            </h6>
            <span class="text-muted font-weight-normal small" v-translate>
              (Optional)
            </span>
            <CTextarea
              v-model="personalMessage"
              :placeholder="$gettext('Add a personal note for the invitation email...')"
              :maxlength="1000"
              rows="4"
            />
            <small class="text-muted">
              {{ personalMessage.length }}/1000
            </small>
          </div>
        </CCol>
      </CRow>
      
      <div class="mt-3 d-flex justify-content-end">
        <CButton
          color="light"
          class="mr-2"
          @click="handleReset"
          :disabled="emails.length === 0 && !currentInput"
        >
          <translate translate-context="collaborators.invite">Reset</translate>
        </CButton>
        <CButton
          color="primary"
          @click="handleInvite"
          :disabled="!canInvite"
        >
          <CIcon name="cipSend" size="sm" class="mr-1"/>
          <translate translate-context="collaborators.invite">Invite</translate>
        </CButton>
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import {sortRolesByPriority} from '../utils/roles'
import CollaboratorScope from '@/domain/core/constant/collaboratorScope'

export default {
  name: 'InviteBlock',
  
  props: {
    scope: {
      type: Object,
      required: true
    },
    availableRoles: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      CollaboratorScope,
      emails: [],
      currentInput: '',
      selectedRoles: {},
      personalMessage: '',
      emailError: '',
      suggestions: [],
      searchDebounce: null,
      requireAcceptance: false, // Default: skip email if permission allows
      selectedSuggestionIndex: -1 // Track selected suggestion for keyboard navigation
    }
  },
  
  computed: {
    ...mapGetters('collaborators', [
      'collaborators'
    ]),
    
    canInvite() {
      return this.emails.length > 0 && Object.values(this.selectedRoles).some(v => v)
    },
    
    canBypassAccept() {
      // Check permission based on scope
      if (this.scope.type === 'pica-service') {
        return this.hasPerm('collaborator.bypass_accept')
      } else if (this.scope.type === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.bypass_accept')
      } else if (this.scope.type === 'global') {
        return this.hasPerm('admin.collaborator.bypass_accept')
      }
      return false
    },
    
    sortedRoles() {
      // Ordina i ruoli usando la utility
      return sortRolesByPriority(this.availableRoles, [])
    },
    
    displayedRoles() {
      // Mostra i primi 20 ruoli ordinati
      return this.sortedRoles.slice(0, 20)
    },
    
    hasMoreRoles() {
      return this.sortedRoles.length > 20
    },
    
    additionalRolesCount() {
      return Math.max(0, this.sortedRoles.length - 20)
    }
  },
  
  methods: {
    ...mapActions('collaborators', [
      'inviteCollaborator',
      'inviteMultipleCollaborators',
      'fetchCollaborators',
      'searchUsers',
      'fetchRolePermissions'
    ]),
    
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email)
    },
    
    handleAddEmail() {
      // If there's a highlighted suggestion (user navigated with arrows), select it
      if (this.suggestions.length > 0 && this.selectedSuggestionIndex >= 0) {
        this.selectSuggestion(this.suggestions[this.selectedSuggestionIndex])
        return
      }

      if (!this.currentInput.trim()) return

      const emailsToAdd = this.currentInput
        .split(/[\n,;]+/)
        .map(e => e.trim())
        .filter(e => e)

      const validEmails = []
      const invalidEmails = []
      const duplicateEmails = []

      for (const email of emailsToAdd) {
        if (!this.validateEmail(email)) {
          invalidEmails.push(email)
        } else if (this.emails.includes(email)) {
          duplicateEmails.push(email)
        } else {
          validEmails.push(email)
        }
      }

      // Aggiungi le email valide
      if (validEmails.length > 0) {
        this.emails.push(...validEmails)
      }

      // Mostra errori se presenti
      if (invalidEmails.length > 0) {
        this.emailError = this.$pgettext('collaborators.error', 'Invalid email address: ') + invalidEmails.join(', ')
      } else if (duplicateEmails.length > 0) {
        this.emailError = this.$pgettext('collaborators.error', 'Email already added: ') + duplicateEmails.join(', ')
      } else {
        this.emailError = ''
      }

      this.currentInput = ''
      this.suggestions = []
      this.selectedSuggestionIndex = -1
    },
    
    removeEmail(index) {
      this.emails.splice(index, 1)
    },
    
    handleBackspace() {
      if (this.currentInput === '' && this.emails.length > 0) {
        this.emails.pop()
      }
    },
    
    handleInput() {
      this.emailError = ''
      this.selectedSuggestionIndex = -1

      // Clear previous debounce
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce)
      }

      // Debounce search
      this.searchDebounce = setTimeout(() => {
        this.searchCollaborators()
      }, 300)
    },

    handleSpecialKeys(event) {
      // Handle dropdown navigation
      if (this.suggestions.length > 0) {
        // Arrow Down: move selection down
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          this.selectedSuggestionIndex = Math.min(
            this.selectedSuggestionIndex + 1,
            this.suggestions.length - 1
          )
          return
        }

        // Arrow Up: move selection up
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, 0)
          return
        }

        // Tab: select first or currently selected suggestion
        if (event.key === 'Tab') {
          event.preventDefault()
          const index = this.selectedSuggestionIndex >= 0 ? this.selectedSuggestionIndex : 0
          this.selectSuggestion(this.suggestions[index])
          return
        }

        // Escape: clear suggestions
        if (event.key === 'Escape') {
          event.preventDefault()
          this.suggestions = []
          this.selectedSuggestionIndex = -1
          return
        }
      }
    },
    
    handlePaste(event) {
      event.preventDefault()
      const pastedText = (event.clipboardData || window.clipboardData).getData('text')
      this.currentInput = pastedText
      this.handleAddEmail()
    },
    
    async searchCollaborators() {
      // Require at least 3 characters
      if (!this.currentInput || this.currentInput.length < 3) {
        this.suggestions = []
        return
      }

      try {
        // Usa l'endpoint di ricerca del backend
        const results = await this.searchUsers({
          searchTerm: this.currentInput,
          excludeExisting: true
        })

        // Filtra utenti già aggiunti
        this.suggestions = (results || [])
          .filter(user => !this.emails.includes(user.email))
          .slice(0, 5) // Limit to 5 suggestions
      } catch (error) {
        this.suggestions = []
      }
    },
    
    selectSuggestion(suggestion) {
      this.emails.push(suggestion.email)
      this.currentInput = ''
      this.suggestions = []
      this.selectedSuggestionIndex = -1
      this.emailError = ''
    },
    
    async handleInvite() {
      if (!this.canInvite) return
      
      const roleIds = Object.keys(this.selectedRoles)
        .filter(id => this.selectedRoles[id])
        .map(id => ({id: parseInt(id)}))
      
      try {
        if (this.emails.length === 1) {
          // Invito singolo
          await this.inviteCollaborator({
            email: this.emails[0],
            roles: roleIds,
            personalMessage: this.personalMessage || undefined,
            sendInvitation: !this.canBypassAccept || this.requireAcceptance
          })
          
          await this.notifySuccess(this.$pgettext('collaborators.notification', 'Invitation sent successfully'))
        } else {
          // Invito multiplo usando bulk endpoint
          const result = await this.inviteMultipleCollaborators({
            emails: this.emails,
            roles: roleIds,
            personalMessage: this.personalMessage || undefined,
            sendInvitation: !this.canBypassAccept || this.requireAcceptance
          })
          
          // Gestisce success e errors dalla risposta
          if (result.success && result.success.length > 0) {
            await this.notifySuccess(
              this.$pgettext('collaborators.notification', '{count} invitations sent successfully')
                .replace('{count}', result.success.length)
            )
          }
          
          if (result.errors && Object.keys(result.errors).length > 0) {
            // Mostra errori specifici per ogni email
            const errorMessages = Object.entries(result.errors)
              .map(([email, error]) => `${email}: ${error}`)
              .join('\n')
            await this.notifyError(
              this.$pgettext('collaborators.notification', 'Some invitations failed:\n') + errorMessages
            )
          }
        }
        
        // Reset form
        this.handleReset()
        
        // Refresh list
        await this.fetchCollaborators()
        
        this.$emit('invited')
      } catch (error) {
        await this.notifyError(this.$pgettext('collaborators.notification', 'Failed to send invitations'))
      }
    },
    
    handleReset() {
      this.emails = []
      this.currentInput = ''
      this.selectedRoles = {}
      this.personalMessage = ''
      this.emailError = ''
      this.suggestions = []
      this.selectedSuggestionIndex = -1
      this.requireAcceptance = false
    },
    
    openRoleSelectionModal() {
      // Emette un evento per far aprire la modale nel componente padre
      // Passiamo un oggetto fittizio per la selezione dei ruoli nell'invito
      this.$emit('open-invite-role-modal', {
        selectedRoles: this.selectedRoles,
        availableRoles: this.availableRoles
      })
    },
    
    updateSelectedRoles(newSelectedRoles) {
      // Metodo per aggiornare i ruoli selezionati dalla modale
      this.selectedRoles = { ...newSelectedRoles }
    },
    
    async showRolePermissions(role) {
      // Emette evento al componente padre per mostrare i permessi del ruolo
      const permissions = await this.fetchRolePermissions(role.id)
      this.$parent.$refs.rolePermissionsModal.show(permissions, role)
    }
  }
}
</script>

<style scoped>
.email-tags-container {
  position: relative;
}

.email-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-height: 38px;
  padding: 4px;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  background: white;
  cursor: text;
}

.email-tags:focus-within {
  border-color: #768192;
  box-shadow: 0 0 0 0.2rem rgba(39, 41, 43, 0.15);
}

.email-tag {
  display: inline-flex;
  align-items: center;
  margin: 2px;
  padding: 4px 8px;
  background: #f0f3f5;
  border: 1px solid #d8dbe0;
  border-radius: 3px;
  font-size: 14px;
}

.tag-remove {
  margin-left: 6px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #768192;
  display: inline-flex;
  align-items: center;
}

.tag-remove:hover {
  color: #e55353;
}

.tag-input {
  flex: 1;
  min-width: 200px;
  margin: 2px;
  padding: 4px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
}

.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #d8dbe0;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.suggestion-item {
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.suggestion-item:hover,
.suggestion-item.selected {
  background: #f0f3f5;
}

.suggestion-item.selected {
  background: #e3f2fd;
}

.permissions-list {
  max-height: 200px;
  overflow-y: auto;
  padding-right: 10px;
}

.role-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 32px;
}

.role-button:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.role-indicator {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  background: white;
  position: relative;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.role-button.role-active .role-indicator {
  background: #10b981;
  border-color: #10b981;
}

.role-button.role-active .role-indicator::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 10px;
  font-weight: bold;
}
</style>